import {
  SET_SETTINGS,
  SET_SETTINGS_DATA_STATUS,
  SET_FEATURES_DATA_STATUS,
  SET_FEATURES,
  SET_CREDENTIALS,
} from "@/store/mutation-types";
import CacheService from "@/services/CacheService";
import { RequestMethod } from "@/types/request-method";
import { SettingsState, Settings, Credentials, Features } from "@/types/settings";
import { RootState } from "@/types/root-state";
import { DataStatus } from "@/types/common";
import { SettingsItemInitialState, FeaturesItemInitialState } from "@/store/state";
import * as Msg from "@/config/notification";
import { ALERT_MAX } from "@/config/alert";
import { PREFIX_MAX } from "@/config/series";
import { setNotificationError } from "@/utils/notification";
import { isAdminUsingProd } from "@/utils/admin";
import { Module, MutationTree, ActionTree, GetterTree } from "vuex";
import Vue from "vue";

const namespaced = true;

const state: SettingsState = {
  settings: {},
  settingsStatus: {},
  features: {},
  featuresStatus: {},
};

const mutations: MutationTree<SettingsState> = {
  [SET_SETTINGS]: (state, payload: { cacheID: number; settings: Settings }) => {
    Vue.set(state.settings, payload.cacheID, payload.settings);
  },
  [SET_SETTINGS_DATA_STATUS]: (state, payload: { cacheID: number; status: DataStatus }) => {
    Vue.set(state.settingsStatus, payload.cacheID, payload.status);
  },
  [SET_FEATURES]: (state, payload: { cacheID: number; features: Features }) => {
    Vue.set(state.features, payload.cacheID, payload.features);
  },
  [SET_FEATURES_DATA_STATUS]: (state, payload: { cacheID: number; status: DataStatus }) => {
    Vue.set(state.featuresStatus, payload.cacheID, payload.status);
  },
  [SET_CREDENTIALS]: (state, payload: { cacheID: number; credentials: Credentials[] }) => {
    if (state.settings[payload.cacheID]) {
      state.settings[payload.cacheID].credentials = payload.credentials;
    }
  },
};

const actions: ActionTree<SettingsState, RootState> = {
  fetch({ commit, dispatch, state, rootState }) {
    const cacheID = rootState.activeCacheID;
    if (state.settingsStatus[cacheID] === DataStatus.FETCHED || state.settingsStatus[cacheID] === DataStatus.FETCHING) {
      // Data already fetched or fetching. Do not fetch
      return;
    }

    // Data not fetched, let's fetch
    commit(SET_SETTINGS_DATA_STATUS, {
      cacheID: cacheID,
      status: DataStatus.FETCHING,
    });

    return CacheService.request(`caches/${cacheID}/info`, RequestMethod.GET)
      .then(response => {
        const settings: Settings = response.data;
        commit(SET_SETTINGS, { cacheID: cacheID, settings: settings });

        commit(SET_SETTINGS_DATA_STATUS, {
          cacheID: cacheID,
          status: DataStatus.FETCHED,
        });
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.SETTINGS_ERROR} (${error})`);

        commit(SET_SETTINGS_DATA_STATUS, {
          cacheID: cacheID,
          status: DataStatus.FAILED,
        });
      });
  },
  fetchFeatures({ commit, dispatch, state, rootState }) {
    const cacheID = rootState.activeCacheID;
    if (state.featuresStatus[cacheID] === DataStatus.FETCHED || state.featuresStatus[cacheID] === DataStatus.FETCHING) {
      // Data already fetched or fetching. Do not fetch
      return;
    }

    // Data not fetched, let's fetch
    commit(SET_FEATURES_DATA_STATUS, {
      cacheID: cacheID,
      status: DataStatus.FETCHING,
    });

    return CacheService.request(`caches/${cacheID}/flags`, RequestMethod.GET)
      .then(response => {
        const features: Features = response.data;
        features["alert-max"] = ALERT_MAX;
        features["prefix-max"] = PREFIX_MAX;
        commit(SET_FEATURES, { cacheID: cacheID, features: features });

        commit(SET_FEATURES_DATA_STATUS, {
          cacheID: cacheID,
          status: DataStatus.FETCHED,
        });
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.FEATURES_ERROR} (${error})`);

        commit(SET_FEATURES_DATA_STATUS, {
          cacheID: cacheID,
          status: DataStatus.FAILED,
        });
      });
  },
  toggleIntrospection({ commit, dispatch, state, rootState }) {
    if (isAdminUsingProd()) return;
    const cacheID = rootState.activeCacheID;
    const features = state.features[cacheID];

    if (!(features["debug-mode"] === 2 || features["debug-mode"] === -2)) {
      return;
    }

    return CacheService.request(`caches/${cacheID}/introspection/toggle`, RequestMethod.POST)
      .then(() => {
        features["debug-mode"] = features["debug-mode"] === 2 ? -2 : 2;
        commit(SET_FEATURES, features);
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.TOGGLE_INTROSPECTION_ERROR} (${error})`);
      });
  },
  fetchCredentials({ commit, dispatch, rootState }) {
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/credentials`, RequestMethod.GET)
      .then(response => {
        const credentials: Credentials[] = response.data;
        commit(SET_CREDENTIALS, { cacheID: cacheID, credentials: credentials });
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.CREDENTIALS_FETCH_ERROR} (${error})`);
      });
  },
  addCredentials({ dispatch, rootState }) {
    if (isAdminUsingProd()) return;
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/credentials`, RequestMethod.POST)
      .then(() => {
        dispatch("fetchCredentials");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.CREDENTIALS_ADD_ERROR} (${error})`);
      });
  },
  removeCredentials({ dispatch, rootState }, username) {
    if (isAdminUsingProd()) return;
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/credentials/${username}`, RequestMethod.DELETE)
      .then(() => {
        dispatch("fetchCredentials");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.CREDENTIALS_REMOVE_ERROR}: ${username} (${error})`);
      });
  },
  promoteCredentials({ dispatch, rootState }, username) {
    if (isAdminUsingProd()) return;
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/credentials/primary/${username}`, RequestMethod.POST)
      .then(() => {
        dispatch("fetchCredentials");
      })
      .catch(error => {
        setNotificationError(dispatch, `${Msg.CREDENTIALS_PROMOTE_ERROR}: ${username} (${error})`);
      });
  },
  setCredentialsCapabilities({ dispatch, rootState }, { username, capabilities }) {
    if (isAdminUsingProd()) return;
    const cacheID = rootState.activeCacheID;
    return CacheService.request(`caches/${cacheID}/credentials/${username}`, RequestMethod.PATCH, {}, capabilities)
      .then()
      .catch(error => {
        setNotificationError(dispatch, `${Msg.CREDENTIALS_CAPABILITIES_ERROR} (${error})`);
      });
  },
};

const getters: GetterTree<SettingsState, RootState> = {
  activeCacheSettings: (state, getters, rootState) => {
    if (state.settings[rootState.activeCacheID]) {
      return state.settings[rootState.activeCacheID];
    }
    return new SettingsItemInitialState(); // defaults
  },
  activeCacheFeatures: (state, getters, rootState) => {
    if (state.features[rootState.activeCacheID]) {
      return state.features[rootState.activeCacheID];
    }
    return new FeaturesItemInitialState(); // defaults
  },
  // Loading strategy:
  // - if status = FETCHED, data is ready
  // - else if status = FAILED, no data available
  // - else loading (status = UNFETCHED or status = FETCHING)
  activeCacheSettingsStatus: (state, getters, rootState) => {
    if (state.settingsStatus[rootState.activeCacheID]) {
      return state.settingsStatus[rootState.activeCacheID];
    }
    return DataStatus.UNFETCHED;
  },
  activeCacheFeaturesStatus: (state, getters, rootState) => {
    if (state.featuresStatus[rootState.activeCacheID]) {
      return state.featuresStatus[rootState.activeCacheID];
    }
    return DataStatus.UNFETCHED;
  },
};

export const settings: Module<SettingsState, RootState> = {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
